import { useState, useEffect } from "react";
import ProductIntro from "@/components/sections/ProductIntro";
import Hero from "@/components/sections/Hero";
import ProductTabs from "@/components/sections/ProductTabs";
import PurchaseCardDesktop from "@/components/sections/PurchaseCardDesktop";
import PurchaseCardMobile from "@/components/sections/PurchaseCardMobile";
import DesktopPurchaseStickyBanner from "@/components/sections/DesktopPurchaseStickyBanner";
import VideoDialog from "@/components/common/VideoDialog";
import SignInModal from "@/components/modals/SignInModal";
import FloatingCountdown from "@/components/common/FloatingCountdown";
import { MetaHead } from "@/components/common/MetaHead";
import { useAuth } from "@/lib/auth-context";
import { useNavigate, useSearchParams } from "react-router-dom";
import { handleCheckout } from "@/lib/checkout";
import { useTranslation } from "react-i18next";
import { PromoBanner } from "@/components/common/PromoBanner";
import { PromoOverlay } from "@/components/common/PromoOverlay";
import { usePromoCode } from "@/hooks/use-promo-code";
import { track, EVENTS } from "@/lib/mixpanel";
import { useTracking } from "@/hooks/use-tracking";
import { usePlans } from "@/hooks/use-plans";

const Index = () => {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [isSignInModalOpen, setIsSignInModalOpen] = useState(false);
  const [pendingPlanId, setPendingPlanId] = useState<string | null>(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { referralCode, eventCode, setReferralCode } = usePromoCode();
  const { plans } = usePlans();
  const { trackCheckout } = useTracking();
  const [activeTab, setActiveTab] = useState("overview");
  const [isFloatingCountdownVisible, setIsFloatingCountdownVisible] = useState(false);
  const [isStickyBannerVisible, setIsStickyBannerVisible] = useState(false);
  const [isPromoOverlayOpen, setIsPromoOverlayOpen] = useState(false);
  const [hasAutoShownPromo, setHasAutoShownPromo] = useState(false);

  useEffect(() => {
    if (hasAutoShownPromo || referralCode) return;

    const handleScroll = () => {
      const scrollY = window.scrollY;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;

      const maxScroll = scrollHeight - clientHeight;
      if (maxScroll <= 0) return;

      const scrollPercent = scrollY / maxScroll;

      if (scrollPercent >= 0.33) {
        setIsPromoOverlayOpen(true);
        setHasAutoShownPromo(true);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasAutoShownPromo, referralCode, eventCode]);

  const handlePurchase = async (planId: string) => {
    // Find the plan data for tracking
    const plan = plans.find(p => p.id === planId);

    // Track purchase intent (Mixpanel + Meta)
    if (plan) {
      trackCheckout(plan, { referralCode, eventCode });
    }

    // Check checkout mode
    const checkoutMode = import.meta.env.VITE_CHECKOUT_MODE || 'login_required';

    // If user is not signed in and login is required, show sign-in modal
    // TODO: the pendingPlanId is too complicated now. It is set multiple times
    setPendingPlanId(planId);
    if (!user?.id && checkoutMode === 'login_required') {
      setIsSignInModalOpen(true);
      return;
    }

    // checkout flow (works with or without user in login_optional mode)
    await handleCheckout({
      planId, // TODO: planId is already stored in localStorage. Deprecate this for clarity
      userId: user?.id,
      navigate,
      setIsLoading: setIsCheckoutLoading,
      allowLoginOptional: checkoutMode === 'login_optional'
    });
  };

  const handleSignInSuccess = async (signedInUser?: any) => {
    // If there's a pending plan purchase, continue with checkout
    if (pendingPlanId) {
      // Use the signed-in user data directly, or fallback to context user
      const userToUse = signedInUser || user;

      await handleCheckout({
        planId: pendingPlanId,
        userId: userToUse?.id,
        navigate,
        setIsLoading: setIsCheckoutLoading
        // Note: we might lose email here if sign-in modal is used, but for now we just handle checkout flow
      });
      setPendingPlanId(null);
    }
  };

  const handleCloseSignInModal = () => {
    setIsSignInModalOpen(false);
    setPendingPlanId(null);
  };

  const handleTabChange = (value: string) => {
    // scroll to the top of the tab content
    setTimeout(() => {
      const tabContent = document.querySelector(`[data-radix-popper-content-wrapper] [value="${value}"]`) ||
        document.querySelector(`[role="tabpanel"][data-state="active"]`) ||
        document.getElementById(value);
      tabContent?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  }

  const handleScrollToPlans = () => {
    const plansSection = document.getElementById('plans-section');
    if (plansSection) {
      plansSection.scrollIntoView({ behavior: 'smooth' });
    }
  }

  useEffect(() => {
    track(EVENTS.VIEW_PRODUCT_PAGE);
  }, []); // Track on initial page load

  // useEffect(() => {
  //   // Check for purchase action in URL
  //   const action = searchParams.get('action');
  //   const planId = searchParams.get('planId');

  //   if (action === 'purchase' && planId) {
  //     // Clear the URL parameters
  //     setSearchParams({}, { replace: true });
  //     // Continue with checkout
  //     handlePurchase(planId);
  //   }
  // }, [searchParams, setSearchParams]);

  useEffect(() => {
    const sentinel = document.getElementById('tabs-sentinel');
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry) {
          // Only show the banner if the sentinel is NOT intersecting AND it's above the viewport
          // (i.e., the user has scrolled past it).
          const isAboveViewport = entry.boundingClientRect.top <= 60;
          setIsStickyBannerVisible(!entry.isIntersecting && isAboveViewport);
        }
      },
      { threshold: 0, rootMargin: '-60px 0px 0px 0px' } // -60px because the Banner is roughly 64px high
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <MetaHead
        title={t('meta.index.title')}
        description={t('meta.index.description')}
      />

      {isCheckoutLoading && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center">
          <div className="bg-white rounded-lg p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto"></div>
          </div>
        </div>
      )}

      <VideoDialog
        isOpen={isVideoPlaying}
        onClose={() => setIsVideoPlaying(false)}
        videoUrl={t('assets.promoVideo')}
      />

      <SignInModal
        isOpen={isSignInModalOpen}
        onClose={handleCloseSignInModal}
        onSignInSuccess={handleSignInSuccess}
      />

      <Hero
        onPlayVideo={() => setIsVideoPlaying(true)}
        onPurchase={handlePurchase}
      />

      {/* Main Content with Purchase Card */}
      <div className="relative bg-slate-50">
        <div className="container mx-auto px-0">
          <div className="grid lg:grid-cols-12 lg:gap-0 xl:gap-8 pt-6">
            {/* Main Content */}
            <div className="lg:col-span-8 xl:col-span-7 lg:col-start-0 xl:col-start-2 pb-16 z-50">
              <ProductIntro />
              <div id="tabs-sentinel" className="h-1" />
              <ProductTabs onPurchase={handlePurchase} activeTab={activeTab} setActiveTab={setActiveTab} onTabChange={handleTabChange} isFloatingCountdownVisible={isFloatingCountdownVisible} />
            </div>

            {/* Purchase Card - Desktop */}
            <PurchaseCardDesktop onPurchase={handlePurchase} />
          </div>
        </div>

        {/* Purchase Card - Mobile */}
        <PurchaseCardMobile
          onPurchase={handlePurchase}
          onScrollToPlans={handleScrollToPlans}
          isFloatingCountdownVisible={isFloatingCountdownVisible}
          onOpenPromo={() => setIsPromoOverlayOpen(true)}
        />
      </div>

      <DesktopPurchaseStickyBanner
        isVisible={isStickyBannerVisible}
        onPurchase={handlePurchase}
        onScrollToPlans={handleScrollToPlans}
      />

      <PromoBanner
        className="hidden lg:flex"
        isVisible={!isFloatingCountdownVisible}
        onOpen={() => setIsPromoOverlayOpen(true)}
      />

      <FloatingCountdown onVisibilityChange={setIsFloatingCountdownVisible} className="hidden lg:block" />

      <PromoOverlay
        isOpen={isPromoOverlayOpen}
        onClose={() => setIsPromoOverlayOpen(false)}
        onRevealCode={(email: string) => {
          // @ts-ignore
          setReferralCode('WELCOME10');
          if (email) {
            localStorage.setItem('checkoutEmail', email);

            const apiUrl = import.meta.env.VITE_WORKER_URL || '';
            fetch(`${apiUrl}/api/kit-subscribe`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email, language: i18n.language })
            }).catch(console.error);
          }
          if (!localStorage.getItem('clPromoWelcome10Claim')) {
            localStorage.setItem('clPromoWelcome10Claim', Date.now().toString());
          }
        }}
        onComplete={() => {
          setIsPromoOverlayOpen(false);
          // handlePurchase('annual');
        }}
      />
    </div>
  );
};

export default Index;
